/* ================================================================ */
// STORY EDITOR COMPONENT | StoryEditor.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
// Actions
import {updatePanelOrder, deletePanel, createStory, updateStory} from '../../actions/Data/Content';
// Selectors
import {getLoadingStatusSelector, getActivePanels, getGlobe} from '../../selectors/Data';
// Sortable list
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
// Material UI
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import makeStyles from '@material-ui/styles/makeStyles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ReorderIcon from '@material-ui/icons/Reorder';
// Hooks
import usePrevious from '@myglobe/core/dist/hooks/usePrevious';
// Local Components
import PanelEditor from './PanelEditor';
import {isNullOrUndefined} from 'util';
import { Paper } from '@material-ui/core';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        //position: 'relative',
        overflow: 'hidden',
        width: '100%',
        //height: '100%',
        //display: 'flex'
    },
    editToggle: {
        position: 'absolute',
        right: '-1em',
        top: '3em'
    },
    // === Panel Classes ===
    panel: {
        position: 'relative',
        overflow: 'hidden',
        color: theme.palette.text.primary,
        width: '100%',
        height: '100%',
        borderRadius: 0
    },
    editor: {
        color: theme.palette.common.black,
        color:'#FFF',
        position:'absolute',
        left:'50%',
        top:'10%',
        width:'25vw',
        minHeight:'50vw',
        overflowY:'scroll'
    },

}));
/* ========================================== */
/**
 * Story Editor
 * ------------
 * Interface for editing slugs, titles and panel order
 * of a story
 *
 * @param {object} props component properties
 * @param {object} globe globe state
 * @param {object} story story data
 * @param {object} panels story panels data
 * @param {boolean} isOpen open status
 * @param {boolean} isLoading is loading status
 * Functions
 * @param {func} onSave save all panel states to store
 * @param {func} onCancel cancel update callback
 * 
 * @param {func} updateStory update story
 * @param {func} createStory create story
 * @param {func} updatePanelOrder bulk panel order update to store
 * @param {func} deletePanel delete panel by panelId
 */
const StoryEditor = (props) => {
    const classes = useStyles();
    const {
        isOpen,
        isLoading,
        globe,
        onSave,
        onCancel,
        updatePanelOrder,
        createStory,
        updateStory,
        deletePanel
    } = props;
    const [story, setStory] = useState(props.story);
    const [panels, setPanels] = useState(props.panels);
    const [panel, setPanel] = useState(null);
    const [confirm, setConfirm] = useState({open: false, callback: ()=>{}});

    useEffect(() => {
        let isSubscribed = true;
        isSubscribed && setPanels(props.panels);
        return () => {
            isSubscribed = false;
        };
    }, [props.panels]);
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Save
     * -----------
     * Send the updated story data to parent via callback
     * Save story data to reducer
     */
    const handleSave = () => {
        if(onSave) onSave(story);
        if(isNullOrUndefined(story.uuid))
            createStory(story, globe.uuid);
        else {
            updateStory(story, globe.name);
            updatePanelOrder && updatePanelOrder(panels);
        }
    };
    const handleCancel = () => {
        onCancel && onCancel();
    };
    /**
     * Handle Panel Edit
     * -----------------
     * Toggles panel editor for given panel
     * index
     * @param {number} index panel index
     */
    const handlePanelEdit = (index) => {
        setPanel(panels[index]);
    };
    /**
     * Handle Panel Edit Cancel
     * ------------------------
     * Cancel panel editor edits without
     * saving
     */
    const handlePanelEditCancel = () => {
        setPanel(null);
    };
    const handlePanelEditSave = () => {
        setPanel(null);
    };
    /**
     * Handle Panel Delete
     * -------------------
     * Triggers the deletion process of the panel
     * via a dialog with the delete panel action callback
     *
     * @param {string} uuid deleting panel's uuid
     */
    const handlePanelDelete = (uuid) => {
        setConfirm({
            open: true,
            callback: ()=>deletePanel(uuid, story.uuid)
        });
    };
    /**
     * Handle Panel Add
     * ----------------
     * Toggles a blank panel in the panel editor
     */
    const handlePanelAdd = () => {
        setPanel({
            position: panels.length,
            slug: '',
            title: 'New Panel',
            content: '',
            viewState: {}
        });
    };
    const handleConfirmCancel = () => {
        setConfirm({open: false, callback: ()=>{}});
    };
    const handleConfirm = () => {
        confirm.callback();
        setConfirm({open: false, callback: ()=>{}});
    };
    /* ========================================== */
    /**
     * Handle Value
     * ------------
     * Update the text value of the story data
     * corresponding to the field name
     *
     * @param {*} event
     */
    const handleValue = (event) => {
        const value = event.target.value;
        const stateName = event.target.name;
        setStory({
            ...story,
            [stateName]: value
        });
    };

    const handleSort = ({oldIndex, newIndex}) => {
        let sortedArr = panels.slice();
        sortedArr.splice(newIndex, 0, sortedArr.splice(oldIndex, 1)[0]);
        sortedArr.forEach((item, i)=>item.position = i);
        console.log(sortedArr);
        setPanels(sortedArr);
        //setPanels(sortedArr);
    };

    return (
        <>
            <Paper
                key="story-editor-dialog"
                style={{display:isOpen?"block":"none"}}
                className={classes.editor}
            ><div>
                {isLoading ?
                    <DialogContent>
                        <div className={classes.loadingContainer}>
                            <CircularProgress color="primary" size={200} />
                        </div>
                    </DialogContent>
                    : (
                        <>
                            <DialogTitle>
                                Story Editor | {story.title}
                            </DialogTitle>
                            <TextField
                                size="small"
                                margin="dense"
                                label="Slug"
                                name="slug"
                                required={true}
                                value={story.slug}
                                variant="outlined"
                                onChange={handleValue}
                            />
                            <TextField
                                size="small"
                                margin="dense"
                                label="Title"
                                name="title"
                                required={true}
                                value={story.title}
                                variant="outlined"
                                onChange={handleValue}
                            />
                            {panels.length > 0 ?
                                <SortableList
                                    onSortEnd={handleSort}
                                    useDragHandle={true}
                                    items={panels}
                                    handleDelete={handlePanelDelete}
                                    handleEdit={handlePanelEdit}
                                    hideSortableGhost={true}
                                />
                                : <Typography>No Panels to Display</Typography>}
                            {story.uuid &&
                            <Button onClick={handlePanelAdd}>Add Panel</Button>
                            }
                            {panel &&
                            <PanelEditor
                                panelsCount={panels.length}
                                isOpen={!isNullOrUndefined(panel)}
                                storyId={story.uuid}
                                panel={panel}
                                panels={panels}
                                onSave={handlePanelEditSave}
                                onCancel={handlePanelEditCancel}
                            />
                            }
                            <DialogActions>
                                <Button onClick={handleCancel}>Cancel</Button>
                                <Button onClick={handleSave}>Save</Button>
                            </DialogActions>
                        </>
                    )}
            </div></Paper>
            <ConfirmationDialog isOpen={confirm.open} onCancel={handleConfirmCancel} onConfirm={handleConfirm} />
        </>
    );
};

const ConfirmationDialog = ({
    isOpen,
    onCancel,
    onConfirm
}) => {
    const classes = useStyles();
    /*const [open, setOpen] = useState(isOpen);
    useEffect(() => {
        let isSubscribed = true;
        isSubscribed && setOpen(isOpen);
        return () => {
            isSubscribed = false;   
        };
    }, [isOpen]);*/
    function handleCancel() {
        onCancel && onCancel();
    }
    function handleConfirm() {
        onConfirm && onConfirm();
    }
    return (
        <Dialog open={isOpen} key="confirmation-dialog">
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </DialogActions>
        </Dialog>
    );
};
ConfirmationDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};

const DragHandle = SortableHandle((classes) => (
    <IconButton aria-label="Reorder" className={classes.button + ' reorderBtn'}>
        <ReorderIcon />
    </IconButton>
));
const SortableItem = SortableElement(({
    item,
    pos,
    classes,
    handleEdit,
    handleDelete
}) => {
    return (
        <ListItem
            dense
            divider
            disableGutters
        >
            <ListItemText primary={item.title} secondary={`/${item.slug}`} />
            <Toolbar disableGutters>
                <IconButton
                    aria-label="Edit"
                    onClick={()=>handleEdit(pos)}
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    aria-label="Delete"
                    onClick={()=>handleDelete(item.uuid)}
                >
                    <DeleteIcon />
                </IconButton>
                <DragHandle classes={classes} />
            </Toolbar>
        </ListItem>
    );
});
const SortableList = SortableContainer(({
    items,
    classes,
    handleEdit,
    handleDelete
}) => (
    <List
        dense
        disablePadding
    >
        {items.map((value, index) => {
            console.log(value, index);
            return (
                <SortableItem
                    key={`item-${value.uuid}`}
                    index={index}
                    pos={index}
                    item={value}
                    classes={classes}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                />
            );
        })}
    </List>
));
/* ======================== */
// PROPS
/* ======================== */
StoryEditor.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    globe: PropTypes.object.isRequired,
    story: PropTypes.shape({
        uuid: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.string,
        panels: PropTypes.array
    }),
    panels: PropTypes.arrayOf(PropTypes.shape({
        uuid: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.string,
        position: PropTypes.number,
        content: PropTypes.string
    })),
    // Functions
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    createStory: PropTypes.func,
    updateStory: PropTypes.func,
    updatePanelOrder: PropTypes.func,
    deletePanel: PropTypes.func
};
StoryEditor.displayName = 'StoryEditor';
/* ========================================== */
// Redux
/* ========================================== */
const mapStateToProps = (store, props) => {    
    const getLoading = getLoadingStatusSelector(['stories', 'globe', 'panels']);
    return {
        isLoading: getLoading(store),
        globe: getGlobe(store),
        panels: getActivePanels(store, props.story.slug),
    };
};
const mapDispatchToProps = dispatch => {
    return bindActionCreators(Object.assign({updatePanelOrder, deletePanel, createStory, updateStory, dispatch}), dispatch);
};
/* ========================================== */
// Export
/* ========================================== */
export default connect(mapStateToProps, mapDispatchToProps)(StoryEditor);